import { useEffect, useState } from "react";
import { usePortalAuth } from "../components/RequireClient.jsx";
import {
  fetchAdminAccessRequests,
  fetchAdminFeedback,
  updateAccessRequestStatus,
  updateFeedbackStatus,
} from "../../api.js";
import "./PublicSupport.css";

const accessStatuses = ["pending", "approved", "rejected"];
const feedbackStatuses = ["submitted", "under_review", "planned", "in_progress", "completed", "rejected"];
const emptyResults = {
  access: { items: [], status: "idle", error: "" },
  feedback: { items: [], status: "idle", error: "" },
};

function display(value, fallback = "—") {
  return value == null || value === "" ? fallback : String(value);
}

function formatCategory(category) {
  return display(category).replaceAll("_", " ");
}

function formatCreatedAt(createdAt) {
  if (!createdAt) return "—";
  const date = new Date(createdAt);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
}

export default function AdminSupportRequests() {
  const { memberRole } = usePortalAuth();
  const [tab, setTab] = useState("access");
  const [results, setResults] = useState(emptyResults);
  const [refresh, setRefresh] = useState(0);
  const allowed = memberRole === "super_admin";

  useEffect(() => {
    if (!allowed) return undefined;

    let active = true;
    const requestType = tab;
    setResults((current) => ({
      ...current,
      [requestType]: { ...current[requestType], status: "loading", error: "" },
    }));

    const request = requestType === "access" ? fetchAdminAccessRequests : fetchAdminFeedback;
    request()
      .then((data) => {
        if (!active) return;
        setResults((current) => ({
          ...current,
          [requestType]: {
            items: Array.isArray(data?.requests) ? data.requests : [],
            status: "loaded",
            error: "",
          },
        }));
      })
      .catch((error) => {
        if (!active) return;
        setResults((current) => ({
          ...current,
          [requestType]: { ...current[requestType], status: "error", error: error?.message || "Unable to load requests" },
        }));
      });

    return () => {
      active = false;
    };
  }, [tab, allowed, refresh]);

  async function update(id, status) {
    try {
      if (tab === "access") await updateAccessRequestStatus(id, status);
      else await updateFeedbackStatus(id, status);
      setRefresh((value) => value + 1);
    } catch (error) {
      setResults((current) => ({
        ...current,
        [tab]: { ...current[tab], error: error?.message || "Unable to update request" },
      }));
    }
  }

  if (!allowed) {
    return <main className="public-page"><section className="public-card"><h1>Access denied</h1></section></main>;
  }

  const { items, status, error } = results[tab];
  const statuses = tab === "access" ? accessStatuses : feedbackStatuses;

  return <main className="public-page"><section className="public-card" style={{ maxWidth: 1100 }}>
    <h1>Support requests</h1>
    <div style={{ display: "flex", gap: 10 }}>
      <button className="support-button" onClick={() => setTab("access")}>Access requests</button>
      <button className="support-button" onClick={() => setTab("feedback")}>User feedback</button>
    </div>
    {error && <p className="support-error">{error}</p>}
    {status !== "loaded" && status !== "error" && <p>Loading requests…</p>}
    {status === "loaded" && items.map((item) => <article key={item.id} style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 18, marginTop: 16 }}>
      <strong>{tab === "access" ? display(item.fullName) : display(item.title)}</strong>
      <p>{tab === "access"
        ? `${display(item.email)} · ${display(item.company)}`
        : `${display(item.email)} · ${display(item.tenantName)} · ${formatCategory(item.category)}`}</p>
      <p><strong>{tab === "access" ? "Reason" : "Description"}:</strong> {display(tab === "access" ? item.reason : item.description)}</p>
      {item.message && <p><strong>Message:</strong> {display(item.message)}</p>}
      {item.pageUrl && <p><strong>Page:</strong> {display(item.pageUrl)}</p>}
      <p>Created {formatCreatedAt(item.createdAt)}</p>
      <label>Status: <select value={item.status || ""} onChange={(event) => update(item.id, event.target.value)}>
        {!statuses.includes(item.status) && <option value="">—</option>}
        {statuses.map((itemStatus) => <option key={itemStatus}>{itemStatus}</option>)}
      </select></label>
    </article>)}
  </section></main>;
}
